import React from "react";
import { ClassNameProps, If, makeClassName, StyleProps, toLocalDateString } from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import { ContractInstallment } from "../../../../Utilities";
import { ContractInstallmentFormat, ContractLineItemFormat } from "../../../Format";
import { ContractInstallmentFormValues } from "../../../FormUtilities";
import { ContractUtilities } from "../../../Utilities";
import styles from "../../ContractLineItem/Table/styles.module.css";

const columnsWithPortion = ["displayOrder", "dueDate", "portion", "price"];
const columnsWithoutPortion = ["displayOrder", "dueDate", "price"];
const columnWidths = { displayOrder: "2.5rem" };

export type ContractInstallmentsTableProps = {
	contractInstallments: Pick<ContractInstallment, "id" | "dueDate" | "portion">[];
	totalPrice: number;
	hidePortion?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function ContractInstallmentsTable({
	contractInstallments,
	totalPrice,
	hidePortion = false,
	className,
	style,
}: ContractInstallmentsTableProps) {
	const totalPortion = React.useMemo(
		() => ContractUtilities.calculateTotalFormPortion(contractInstallments),
		[contractInstallments],
	);

	const columns = hidePortion ? columnsWithoutPortion : columnsWithPortion;

	return (
		<Table
			columns={columns}
			columnWidths={columnWidths}
			className={makeClassName(styles["table"], className)}
			style={style}
		>
			<Table.Header className={styles["table__header"]}>
				<Table.Row>
					<Table.Heading column="displayOrder" noResize>
						#
					</Table.Heading>
					<Table.Heading column="dueDate" noResize>
						Due Date
					</Table.Heading>
					<If condition={!hidePortion}>
						<Table.Heading column="portion" noResize>
							Relative Portion
						</Table.Heading>
					</If>
					<Table.Heading column="price" noResize>
						Price
					</Table.Heading>
				</Table.Row>
			</Table.Header>
			<Table.Body>
				{contractInstallments.map((installment, index) => {
					const portion = installment.portion ?? 0;
					const price = (totalPrice * portion) / totalPortion;

					return (
						<Table.Row key={index} className={styles["table__row"]}>
							<Table.Cell column="displayOrder" oneLine numeric>
								{index + 1}
							</Table.Cell>
							<Table.Cell column="dueDate" oneLine>
								{ContractInstallmentFormat.Fields.dueDate(installment.dueDate)}
							</Table.Cell>
							<If condition={!hidePortion}>
								<Table.Cell column="portion" oneLine numeric>
									{ContractInstallmentFormat.Fields.portion(portion)} /{" "}
									{ContractInstallmentFormat.Fields.portion(totalPortion)}
								</Table.Cell>
							</If>
							<Table.Cell column="price" oneLine numeric>
								{ContractLineItemFormat.Fields.amount(price)}
							</Table.Cell>
						</Table.Row>
					);
				})}
			</Table.Body>
		</Table>
	);
}

export type ContractInstallmentsFormTableProps = Omit<ContractInstallmentsTableProps, "contractInstallments"> & {
	formValuesSets: Pick<ContractInstallmentFormValues.Base, "dueDate" | "portion">[];
};

export function ContractInstallmentsFormTable({ formValuesSets, ...props }: ContractInstallmentsFormTableProps) {
	const contractInstallments = React.useMemo(
		() =>
			formValuesSets.map((formValues, i) => ({
				id: i.toString(),
				dueDate: formValues.dueDate || toLocalDateString(new Date()),
				portion: formValues.portion ?? 0,
			})),
		[formValuesSets],
	);

	return <ContractInstallmentsTable contractInstallments={contractInstallments} {...props} />;
}
